import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogIn, Lock } from 'lucide-react';
import { login, isAuthed } from '@/lib/auth';
import { ADMIN } from '@/constants/testIds';
import { api } from '@/lib/api';
import Logo from '@/components/public/Logo';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [brand, setBrand] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/public/content').then((r) => setBrand(r.data?.brand?.extra || null)).catch(() => {});
  }, []);

  if (isAuthed()) return <Navigate to="/admin/overview" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success('Signed in.');
      navigate('/admin/overview', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Invalid email or password.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-[#0B0C0F] text-white flex items-center justify-center px-4 pg-grain relative overflow-hidden">
      <div aria-hidden className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#F2B705]/15 blur-3xl" />
      <div aria-hidden className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#C9A227]/10 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 mb-4"><Logo brand={brand} invert size="lg" /></div>
          <h1 className="font-display text-3xl leading-tight">Admin Dashboard</h1>
          <p className="mt-2 text-sm text-white/60">Sign in to manage your platform.</p>
        </div>

        <form data-testid={ADMIN.loginForm} onSubmit={onSubmit} className="rounded-2xl border border-white/10 bg-[#141414]/80 backdrop-blur p-6 shadow-[0_18px_50px_rgba(0,0,0,0.5)]">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-white/60">Email</label>
            <input data-testid={ADMIN.loginEmail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" placeholder="you@example.com" className="mt-1 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-white placeholder-white/30 focus:border-[#F2B705] focus:ring-2 focus:ring-[#F2B705]/30 outline-none transition-colors" />
          </div>
          <div className="mt-4">
            <label className="text-xs font-semibold uppercase tracking-widest text-white/60">Password</label>
            <input data-testid={ADMIN.loginPassword} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" placeholder="••••••••" className="mt-1 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-white placeholder-white/30 focus:border-[#F2B705] focus:ring-2 focus:ring-[#F2B705]/30 outline-none transition-colors" />
          </div>
          {error && (
            <div data-testid={ADMIN.loginError} className="mt-4 flex items-start gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
              <Lock className="w-4 h-4 mt-0.5" /> {error}
            </div>
          )}
          <button data-testid={ADMIN.loginSubmit} type="submit" disabled={loading} className="mt-6 w-full inline-flex justify-center items-center gap-2 bg-[#F2B705] hover:bg-[#FFC83D] disabled:opacity-60 text-[#141414] font-semibold px-4 py-3 rounded-xl transition-colors">
            {loading ? 'Signing in...' : (<><LogIn className="w-4 h-4" /> Sign in</>)}
          </button>
        </form>
        <div className="mt-4 text-center"><a href="/" className="text-xs text-white/60 hover:text-white transition-colors">← Back to website</a></div>
      </div>
    </div>
  );
}
